import React, { useContext, useEffect, useRef, useState } from "react";
import { Paper, Button, Typography } from "@mui/material";
import clsx from "clsx";
import { viewportContext } from "../../../context/ViewportContext";

import NarcoticsChart from "./NarcoticsChart";
import NarcoticsDataSummaryTable from "./NarcoticsDataSummaryTable";

import styles from "../../../styles.module.css";

import {
  getUniqueMonths,
  getUniqueQuarters,
  getUniqueYears,
} from "@thequantumhub/iam-package";

const NarcoticsComplianceTable = ({
  narcoticsRecords,
  timeFilter,
  columnView,
  handleColumnViewChange,
  handleTimeFilterChange,
}) => {
  const timeUnits =
    timeFilter === "monthly"
      ? getUniqueMonths(narcoticsRecords)
      : timeFilter === "quarterly"
      ? getUniqueQuarters(narcoticsRecords)
      : getUniqueYears(narcoticsRecords);

  const [aggregatedData, setAggregatedData] = useState({});
  const [showGroupBy, setShowGroupBy] = useState(false);
  const { screenType } = useContext(viewportContext);

  const paperRef = useRef(null);

  const handleClickOutside = (event) => {
    if (paperRef.current && !paperRef.current.contains(event.target)) {
      setShowGroupBy(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const determineAggregatedData = (
    columnView,
    narcoticsRecords,
    timeUnits,
    timeFilter
  ) => {
    switch (columnView) {
      case "unit":
        return aggregateDataByUnit(narcoticsRecords, timeFilter);
      case "facility":
        return aggregateDataByFacility(narcoticsRecords, timeFilter);
      default:
        return aggregateDataByUnit(narcoticsRecords, timeFilter);
    }
  };

  useEffect(() => {
    const newData = determineAggregatedData(
      columnView,
      narcoticsRecords,
      timeUnits,
      timeFilter
    );
    setAggregatedData(newData);
  }, [narcoticsRecords, timeUnits, timeFilter, columnView]);

  // useEffect(() => {
  //   console.log("Aggregated Data:\n ", aggregatedData);
  // }, [aggregatedData]);

  useEffect(() => {
    console.log("Narcotics Records: \n", narcoticsRecords);
  }, [narcoticsRecords]);

  const renderGroupBy = () => {
    return (
      <>
        <Button
          className={clsx(
            columnView === "unit"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("unit")}
        >
          Unit
        </Button>
        <Button
          className={clsx(
            columnView === "facility"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleColumnViewChange("facility")}
        >
          Facility
        </Button>
        <span className={clsx(styles.marginRight2, styles.marginLeft2)}>
          and
        </span>
        <Button
          className={clsx(
            timeFilter === "monthly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("monthly")}
        >
          Month
        </Button>
        <Button
          className={clsx(
            timeFilter === "quarterly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("quarterly")}
        >
          Quarter
        </Button>
        <Button
          className={clsx(
            timeFilter === "yearly"
              ? styles.chartFilterButtonPressed
              : styles.chartFilterButton,
            styles.groupByButtonMargin
          )}
          onClick={() => handleTimeFilterChange("yearly")}
        >
          Year
        </Button>
      </>
    );
  };

  return (
    <>
      <div>
        <Paper className={styles.paperStyles}>
          <div className={styles.chartFilterRow}>
            <div className={styles.graphTitleSection}>
              <Typography variant="h5">Graph</Typography>
            </div>
            <div>
              {screenType.isTab || screenType.isMobile ? (
                <>
                  <Button
                    onClick={() => setShowGroupBy(!showGroupBy)}
                    className={clsx(
                      styles.secondaryButton,
                      styles.complianceTableButtons
                    )}
                  >
                    {screenType.isMobile ? "Group" : "Group By"}
                  </Button>
                  {showGroupBy && (
                    <Paper ref={paperRef} className={styles.groupByPaperStyles}>
                      {renderGroupBy()}
                    </Paper>
                  )}
                </>
              ) : (
                <div className={styles.flexCenter}>
                  <span className={clsx(styles.marginRight5)}>Group By</span>
                  {renderGroupBy()}
                </div>
              )}
            </div>
          </div>

          <NarcoticsChart
            aggregatedData={aggregatedData}
            timeUnits={timeUnits}
            timeFilter={timeFilter}
          />
        </Paper>
      </div>
      <div>
        {aggregatedData ? (
          <NarcoticsDataSummaryTable
            aggregatedData={aggregatedData}
            timeUnits={timeUnits}
          />
        ) : (
          <Paper className={styles.paperStyles}>
            <Typography variant="h5">Fetching Summary ...</Typography>
          </Paper>
        )}
      </div>
    </>
  );
};

const aggregateDataByUnit = (records, timeUnit) => {
  const unitData = {};

  records.forEach((record) => {
    const unit = record.unit;
    const qualitative = record.qualitative;
    const quantitative = record.quantitativeCompliance
      ? record.quantitativeCompliance
      : {};
    const date = new Date(record.createdAt);

    // format time units in a way that could be used for summary tables
    let period;
    switch (timeUnit) {
      case "monthly":
        period = `${date.getMonth() + 1}-${date.getFullYear()}`;
        break;
      case "quarterly":
        period = `Q${
          Math.floor(date.getMonth() / 3) + 1
        }-${date.getFullYear()}`;
        break;
      case "yearly":
        period = `${date.getFullYear()}`;
        break;
      default:
        throw new Error("Invalid time unit");
    }

    if (!unitData[unit]) {
      unitData[unit] = {};
    }

    if (!unitData[unit][period]) {
      unitData[unit][period] = {
        totalMet: 0,
        totalQuestions: 0,
        totalRecords: 0,
        totalPassed: 0,
      };
    }

    // find total number of compliant answers
    const metCount = Object.keys(qualitative).filter(
      (q) => qualitative[q].answer === "met"
    ).length;
    const totalQuestions = Object.keys(qualitative).length - 1; // Excluding the compliance key

    unitData[unit][period].totalMet += metCount;
    unitData[unit][period].totalQuestions += totalQuestions;
    unitData[unit][period].totalRecords += quantitative?.totalRecords
      ? quantitative.totalRecords
      : 0;
    unitData[unit][period].totalPassed += quantitative?.passedRecords
      ? quantitative.passedRecords
      : 0;
  });

  // console.log("Unit Data: ", unitData);

  // store and calculate the complaince results
  const unitCompliance = {};
  for (const unit in unitData) {
    unitCompliance[unit] = {};
    for (const period in unitData[unit]) {
      const data = unitData[unit][period];
      const qualitativeCompliance = (data.totalMet / data.totalQuestions) * 100;
      const qualitativeRecords = `${data.totalMet}/${data.totalQuestions}`;
      const quantitativeCompliance =
        (data.totalPassed / data.totalRecords) * 100;
      const quantitativeRecords = `${data.totalPassed}/${data.totalRecords}`;

      unitCompliance[unit][period] = {
        qualitative_compliance: qualitativeCompliance.toFixed(2),
        qualitative_records: qualitativeRecords,
        quantitative_compliance: quantitativeCompliance.toFixed(2),
        quantitative_records: quantitativeRecords,
      };
    }
  }

  // console.log("Unit Compliance Data: \n", unitCompliance);

  return unitCompliance;
};

const aggregateDataByFacility = (records, timeUnit) => {
  const facilityData = {};

  records.forEach((record) => {
    const facility = record.facility;
    const qualitative = record.qualitative;
    const quantitative = record.quantitativeCompliance || {}; // Added check
    const date = new Date(record.createdAt);

    // format time units in a way that could be used for summary tables
    let period;
    switch (timeUnit) {
      case "monthly":
        period = `${date.getMonth() + 1}-${date.getFullYear()}`;
        break;
      case "quarterly":
        period = `Q${
          Math.floor(date.getMonth() / 3) + 1
        }-${date.getFullYear()}`;
        break;
      case "yearly":
        period = `${date.getFullYear()}`;
        break;
      default:
        throw new Error("Invalid time unit");
    }

    if (!facilityData[facility]) {
      facilityData[facility] = {};
    }

    if (!facilityData[facility][period]) {
      facilityData[facility][period] = {
        totalMet: 0,
        totalQuestions: 0,
        totalRecords: 0,
        totalPassed: 0,
      };
    }

    const metCount = Object.keys(qualitative).filter(
      (q) => qualitative[q].answer === "met"
    ).length;
    const totalQuestions = Object.keys(qualitative).length - 1; // Excluding the compliance key

    facilityData[facility][period].totalMet += metCount;
    facilityData[facility][period].totalQuestions += totalQuestions;
    facilityData[facility][period].totalRecords += quantitative.totalRecords
      ? quantitative.totalRecords
      : 0;
    facilityData[facility][period].totalPassed +=
      quantitative.passedRecords || 0; // Added check
  });

  // console.log("Facility Data: ", facilityData);

  // store and calculate the compliance results
  const facilityCompliance = {};
  for (const facility in facilityData) {
    facilityCompliance[facility] = {};
    for (const period in facilityData[facility]) {
      const data = facilityData[facility][period];
      const qualitativeCompliance = (data.totalMet / data.totalQuestions) * 100;
      const qualitativeRecords = `${data.totalMet}/${data.totalQuestions}`;
      const quantitativeCompliance =
        (data.totalPassed / data.totalRecords) * 100;
      const quantitativeRecords = `${data.totalPassed}/${data.totalRecords}`;

      facilityCompliance[facility][period] = {
        qualitative_compliance: qualitativeCompliance.toFixed(2),
        qualitative_records: qualitativeRecords,
        quantitative_compliance: quantitativeCompliance.toFixed(2),
        quantitative_records: quantitativeRecords,
      };
    }
  }

  return facilityCompliance;
};

export default NarcoticsComplianceTable;
